// @flow

import moment from 'moment';

// This type encapsulates the information needed to be displayed to the user and
// is the common interface between the varied sources that generate notes.
export type Note = {
  // The id of the source.
  _id: string,

  // Identifies the source of the note. Corresponds to NOTE_TYPES below.
  noteType: string,

  // Loose, variant-dependent information to accompany the notes and provide
  // context.
  mainText: string,

  // The notes.
  notes: string,

  // The most recent editor.
  lastEditor: ?string,

  // The most recent edit date.
  lastEditDate: ?Date,
}

export const NOTE_TYPES = {
  PROBLEM: 'PROBLEM',
  MEDICATION: 'MEDICATION',
  INTERVENTION: 'INTERVENTION',
  PROGRESS: 'PROGRESS',
  IMMUNIZATION: 'IMMUNIZATION',
  WCC: 'WCC',
  TESTING: 'TESTING',
  GROWTH: 'GROWTH',
  REPORT: 'REPORT',
};

function getProblemNotes(notes: Note[], patient: Object): void {
  const extractNote = variant => (problem) => {
    const onsetDateStr = problem.onsetDate ? moment(problem.onsetDate).format('DD-MM-YYYY') : 'N/A';
    const resolutionDateStr = problem.resolutionDate ? moment(problem.resolutionDate).format('DD-MM-YYYY') : 'Ongoing';
    notes.push({
      _id: problem._id,
      noteType: NOTE_TYPES.PROBLEM,
      mainText: `${variant} Diagnosis: ${problem.diagnosis} (${onsetDateStr} - ${resolutionDateStr})`,
      notes: problem.notes,
      lastEditor: problem.editors.length ? problem.editors[0] : '',
      lastEditDate: problem.editDates.length ? problem.editDates[0] : null,
    });
  };

  patient.chronicProblems.forEach(extractNote('Chronic'));
  patient.acuteProblems.forEach(extractNote('Acute'));
}

function getMedicationNotes(notes: Note[], patient: Object): void {
  const extractNote = variant => (medication) => {
    const startDateStr = medication.startDate ? moment(medication.startDate).format('DD-MM-YYYY') : 'N/A';
    const endDateStr = medication.endDate ? moment(medication.endDate).format('DD-MM-YYYY') : 'Ongoing';
    notes.push({
      _id: medication._id,
      noteType: NOTE_TYPES.MEDICATION,
      mainText: `${variant} Medication: ${medication.drugName} (${startDateStr} - ${endDateStr})`,
      notes: medication.notes,
      lastEditor: medication.editors.length ? medication.editors[0] : '',
      lastEditDate: medication.editDates.length ? medication.editDates[0] : null,
    });
  };

  patient.chronicMedications.forEach(extractNote('Chronic'));
  patient.acuteMedications.forEach(extractNote('Acute'));
}

function getInterventionNotes(notes: Note[], patient: Object): void {
  const extractNote = variant => (intervention) => {
    const startDateStr = intervention.startDate ? moment(intervention.startDate).format('DD-MM-YYYY') : 'N/A';
    const endDateStr = intervention.endDate ? moment(intervention.endDate).format('DD-MM-YYYY') : 'Ongoing';
    notes.push({
      _id: intervention._id,
      noteType: NOTE_TYPES.INTERVENTION,
      mainText: `${variant} Intervention: ${intervention.name} (${startDateStr} - ${endDateStr})`,
      notes: intervention.notes,
      lastEditor: intervention.editors.length ? intervention.editors[0] : '',
      lastEditDate: intervention.editDates.length ? intervention.editDates[0] : null,
    });
  };

  patient.chronicInterventions.forEach(extractNote('Chronic'));
  patient.acuteInterventions.forEach(extractNote('Acute'));
}

function getNurseAndProviderNotes(notes: Note[], patient: Object): void {
  const extractNote = variant => (note) => {
    const dateStr = note.date ? moment(note.date).format('DD-MM-YYYY') : 'N/A';
    const problem = patient.problemIdToProblem[note.problemId];
    notes.push({
      _id: note._id,
      noteType: NOTE_TYPES.PROGRESS,
      mainText: `${variant} Note for ${problem ? problem.diagnosis : 'N/A'} (${dateStr})`,
      notes: note.notes,
      lastEditor: note.editors.length ? note.editors[0] : '',
      lastEditDate: note.editDates.length ? note.editDates[0] : null,
    });
  };

  patient.progressNotes.nurseNotes.forEach(extractNote('Nurse'));
  patient.progressNotes.providerNotes.forEach(extractNote('Provider'));
}

function getVitalSignNotes(notes: Note[], patient: Object): void {
  patient.progressNotes.vitalSigns.forEach((vitalSign) => {
    const dateStr = vitalSign.date ? moment(vitalSign.date).format('DD-MM-YYYY') : 'N/A';
    notes.push({
      _id: vitalSign._id,
      noteType: NOTE_TYPES.PROGRESS,
      mainText: `Vitals taken on ${dateStr}`,
      notes: vitalSign.notes,
      lastEditor: vitalSign.editors.length ? vitalSign.editors[0] : '',
      lastEditDate: vitalSign.editDates.length ? vitalSign.editDates[0] : null,
    });
  });
}

function getImmunizationNotes(notes: Note[], patient: Object): void {
  const extractNote = (imm) => {
    const dateStr = imm.dateAdministered ? moment(imm.dateAdministered).format('DD-MM-YYYY') : 'N/A';
    notes.push({
      _id: imm._id,
      noteType: NOTE_TYPES.IMMUNIZATION,
      mainText: `Immunization ${imm.immunization} administered on ${dateStr}`,
      notes: imm.notes,
      lastEditor: imm.editors.length ? imm.editors[0] : '',
      lastEditDate: imm.editDates.length ? imm.editDates[0] : null,
    });
  };

  patient.requiredImmunizations.forEach(extractNote);
  patient.additionalImmunizations.forEach(extractNote);
}

function getWccNotes(notes: Note[], patient: Object): void {
  patient.wellChildChecks.forEach((wcc) => {
    const dateStr = wcc.date ? moment(wcc.date).format('DD-MM-YYYY') : 'N/A';
    notes.push({
      _id: wcc._id,
      noteType: NOTE_TYPES.WCC,
      mainText: `${wcc.age} WCC on ${dateStr}`,
      notes: wcc.notes,
      lastEditor: wcc.editors.length ? wcc.editors[0] : '',
      lastEditDate: wcc.editDates.length ? wcc.editDates[0] : null,
    });
  });
}

function getTestNotes(notes: Note[], patient: Object): void {
  // HIV Tests
  const extractHivNote = (hiv) => {
    const dateStr = hiv.date ? moment(hiv.date).format('DD-MM-YYYY') : 'N/A';
    notes.push({
      _id: hiv._id,
      noteType: NOTE_TYPES.TESTING,
      mainText: `HIV Test on ${dateStr} with ${hiv.positive} result`,
      notes: hiv.notes,
      lastEditor: hiv.editors.length ? hiv.editors[0] : '',
      lastEditDate: hiv.editDates.length ? hiv.editDates[0] : null,
    });
  };

  if (patient.testing.admissionHivTest0) {
    extractHivNote(patient.testing.admissionHivTest0);
  }
  if (patient.testing.admissionHivTest6) {
    extractHivNote(patient.testing.admissionHivTest6);
  }
  patient.testing.hivTests.forEach(extractHivNote);

  // TB Tests
  patient.testing.tbTests.forEach((tb) => {
    const dateStr = tb.date ? moment(tb.date).format('DD-MM-YYYY') : 'N/A';
    notes.push({
      _id: tb._id,
      noteType: NOTE_TYPES.TESTING,
      mainText: `TB Test on ${dateStr} with ${tb.positive} result`,
      notes: tb.notes,
      lastEditor: tb.editors.length ? tb.editors[0] : '',
      lastEditDate: tb.editDates.length ? tb.editDates[0] : null,
    });
  });

  // TB Screenings
  patient.testing.tbScreenings.forEach((tb) => {
    const dateStr = tb.date ? moment(tb.date).format('DD-MM-YYYY') : 'N/A';
    notes.push({
      _id: tb._id,
      noteType: NOTE_TYPES.TESTING,
      mainText: `TB Screening on ${dateStr} with ${tb.positive} result`,
      notes: tb.notes,
      lastEditor: tb.editors.length ? tb.editors[0] : '',
      lastEditDate: tb.editDates.length ? tb.editDates[0] : null,
    });
  });

  // HB Tests
  const extractHbNote = (hb) => {
    const dateStr = hb.dateAdministered ? moment(hb.dateAdministered).format('DD-MM-YYYY') : 'N/A';
    notes.push({
      _id: hb._id,
      noteType: NOTE_TYPES.TESTING,
      mainText: `HB Test on ${dateStr} with result ${hb.result}`,
      notes: hb.notes,
      lastEditor: hb.editors.length ? hb.editors[0] : '',
      lastEditDate: hb.editDates.length ? hb.editDates[0] : null,
    });
  };

  if (patient.testing.admissionHemoglobinTest) {
    extractHbNote(patient.testing.admissionHemoglobinTest);
  }
  patient.testing.hemoglobinTests.forEach(extractHbNote);

  // Glucose Tests
  patient.testing.glucoseTests.forEach((glucose) => {
    const dateStr = glucose.date ? moment(glucose.date).format('DD-MM-YYYY') : 'N/A';
    notes.push({
      _id: glucose._id,
      noteType: NOTE_TYPES.TESTING,
      mainText: `Glucose Test on ${dateStr} with result ${glucose.result}`,
      notes: glucose.notes,
      lastEditor: glucose.editors.length ? glucose.editors[0] : '',
      lastEditDate: glucose.editDates.length ? glucose.editDates[0] : null,
    });
  });

  // Syphilis Tests
  patient.testing.syphilisTests.forEach((syphilis) => {
    const dateStr = syphilis.date ? moment(syphilis.date).format('DD-MM-YYYY') : 'N/A';
    notes.push({
      _id: syphilis._id,
      noteType: NOTE_TYPES.TESTING,
      mainText: `Syphilis Test on ${dateStr} with result ${syphilis.positive}`,
      notes: syphilis.notes,
      lastEditor: syphilis.editors.length ? syphilis.editors[0] : '',
      lastEditDate: syphilis.editDates.length ? syphilis.editDates[0] : null,
    });
  });

  // Misc Tests
  patient.testing.miscTests.forEach((misc) => {
    const dateStr = misc.date ? moment(misc.date).format('DD-MM-YYYY') : 'N/A';
    const result = misc.positive && misc.result
      ? `${misc.positive}, ${misc.result}` : `${misc.positive || misc.result || 'N/A'}`;
    notes.push({
      _id: misc._id,
      noteType: NOTE_TYPES.TESTING,
      mainText: `${misc.name} Test on ${dateStr} with result ${result}`,
      notes: misc.notes,
      lastEditor: misc.editors.length ? misc.editors[0] : '',
      lastEditDate: misc.editDates.length ? misc.editDates[0] : null,
    });
  });
}

function getGrowthNotes(notes: Note[], patient: Object): void {
  const extractNote = (growth) => {
    if (!growth.weight && !growth.length) {
      return;
    }
    const dateStr = growth.date ? moment(growth.date).format('DD-MM-YYYY') : 'N/A';
    const measurements = [];
    if (growth.weight) {
      measurements.push(`W=${growth.weight}`);
    }
    if (growth.length) {
      measurements.push(`H=${growth.length}`);
    }
    notes.push({
      _id: growth._id,
      noteType: NOTE_TYPES.GROWTH,
      mainText: `Growth Measurement taken on ${dateStr}: ${measurements.join(', ')}`,
      notes: growth.notes,
      lastEditor: growth.editors.length ? growth.editors[0] : '',
      lastEditDate: growth.editDates.length ? growth.editDates[0] : null,
    });
  };

  patient.requiredMeasurements.forEach(extractNote);
  patient.infantMeasurements.forEach(extractNote);
  patient.childMeasurements.forEach(extractNote);
}

function getExternalReportNotes(notes: Note[], patient: Object): void {
  // Doctor Notes
  patient.externalReports.doctorNotes.forEach((note) => {
    const dateStr = note.date ? moment(note.date).format('DD-MM-YYYY') : 'N/A';
    notes.push({
      _id: note._id,
      noteType: NOTE_TYPES.REPORT,
      mainText: `Doctor's Note: ${note.name} from ${dateStr}`,
      notes: note.notes,
      lastEditor: note.editors.length ? note.editors[0] : '',
      lastEditDate: note.editDates.length ? note.editDates[0] : null,
    });
  });

  // Others
  const extractNote = (note) => {
    const dateStr = note.date ? moment(note.date).format('DD-MM-YYYY') : 'N/A';
    notes.push({
      _id: note._id,
      noteType: NOTE_TYPES.REPORT,
      mainText: `Report: ${note.name} from ${dateStr}`,
      notes: note.notes,
      lastEditor: note.editors.length ? note.editors[0] : '',
      lastEditDate: note.editDates.length ? note.editDates[0] : null,
    });
  };

  patient.externalReports.labResults.forEach(extractNote);
  patient.externalReports.xrays.forEach(extractNote);
  patient.externalReports.ctscans.forEach(extractNote);
  patient.externalReports.ultrasounds.forEach(extractNote);
  patient.externalReports.socialWorks.forEach(extractNote);
  patient.externalReports.miscReports.forEach(extractNote);
}

// Aggregates the various sources of notes from the given patient and appends
// them to the input list.
// TODO(someone): filter results older than XXX days?
export function getAllNotes(notes: Note[], patient: Object): void {
  getProblemNotes(notes, patient);
  getMedicationNotes(notes, patient);
  getInterventionNotes(notes, patient);
  getNurseAndProviderNotes(notes, patient);
  getVitalSignNotes(notes, patient);
  getImmunizationNotes(notes, patient);
  getWccNotes(notes, patient);
  getTestNotes(notes, patient);
  getGrowthNotes(notes, patient);
  getExternalReportNotes(notes, patient);
}



// WEBPACK FOOTER //
// ./client/src/util/aggregatedNotesUtil.js